/**
 * SwipeableCard — phone list card. Drag left to reveal an action tray (72px per action);
 * a plain tap fires onClick. Vertical scrolling is left alone (touch-action: pan-y).
 * actions: [{ icon, label, onClick?, href?, className, testId }]
 */
import React, { useRef, useState } from "react";

const ACTION_W = 72;

export function SwipeableCard({ actions = [], onClick, className = "", innerClassName = "bg-white", children, testId }) {
  const trayWidth = actions.length * ACTION_W;
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef(null);

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    drag.current = {
      startX: e.clientX, startY: e.clientY, startOffset: offset, moved: false, axis: null,
      skipClick: !!e.target.closest("button, a, input, [data-checkbox], [role=checkbox]"),
    };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || !trayWidth) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.axis) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      d.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (d.axis === "x") e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    if (d.axis !== "x") return;
    d.moved = true;
    setOffset(Math.max(-trayWidth, Math.min(0, d.startOffset + dx)));
  };

  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d) return;
    if (d.moved) { setOffset(offset < -trayWidth / 2 ? -trayWidth : 0); return; }
    if (offset !== 0) { setOffset(0); return; }
    if (!d.skipClick) onClick?.();
  };

  return (
    <div className={`relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100 ${className}`} data-testid={testId} data-open={offset !== 0 ? "true" : "false"}>
      {trayWidth > 0 && (
        <div className="absolute inset-y-0 right-0 flex" style={{ width: trayWidth }} data-testid={testId ? `${testId}-actions` : undefined}>
          {actions.map((a) => {
            const Icon = a.icon;
            const cls = `flex flex-col items-center justify-center gap-1 text-[11px] font-semibold select-none ${a.className || "bg-slate-700 text-white"}`;
            const inner = <><Icon className="h-5 w-5" /><span>{a.label}</span></>;
            return a.href ? (
              <a key={a.label} href={a.href} className={cls} style={{ width: ACTION_W }} onClick={(e) => { e.stopPropagation(); setOffset(0); }} data-testid={a.testId}>{inner}</a>
            ) : (
              <button key={a.label} type="button" className={cls} style={{ width: ACTION_W }} onClick={(e) => { e.stopPropagation(); setOffset(0); a.onClick?.(); }} data-testid={a.testId}>{inner}</button>
            );
          })}
        </div>
      )}
      <div
        className={`relative select-none ${innerClassName}`}
        style={{ transform: `translateX(${offset}px)`, transition: dragging ? "none" : "transform 180ms ease-out", touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {children}
      </div>
    </div>
  );
}

/** One-line discoverability hint shown above a swipeable list. */
export function SwipeHint({ text = "Swipe a card left for quick actions · tap to open" }) {
  return <p className="text-[11px] text-slate-400 px-1 pb-2 text-center" data-testid="swipe-hint">{text}</p>;
}
